import { useState, useEffect } from 'react';
import { SiteVideoInline } from "../../siteWidgets/videoPlayer/genericInlineVideoPlayer/siteVideoInline";
import { FrontCopyButton } from "../../commonComponents/inline/frontCopyButton/frontCopyButton";
import { HyperLinkBuilder } from "../../commonComponents/inline/hyperLink";
import { openFacebookShare, openTwitterShare } from "../../widgetsUtils";
import { WhatsAppShareButton } from "../../siteWidgets/socialShareButtons/whatsApp/whatsAppShareButton";
import { SiteVideoData, SiteItemData } from "../../interfaces";

const MIN_MARGIN_FROM_HEADER_AND_FOOTER = 30; // 15px from top and 15px from bottom
const ASPECT_RATIO_9_16 = 9 / 16;

interface VerticalVideoItemProps {
    index: number
    domain: string
    isMobile: boolean
    videoItem: SiteItemData & { hidePrerollAds?: boolean, prerollIndex?: number }
    languageCode: string
    showPrerollAds: boolean
    isTitleVisible: boolean
    isSubTitleVisible: boolean
    isGalleryView?: boolean
    shouldDisplayArrow?: boolean
    currentPlayingVideo?: number
    onNextSlide?: () => void
    onVideoOverlayClose: () => void
}

export const VerticalVideoItem = (props: VerticalVideoItemProps) => {
    const [isSubtitleOpen, setIsSubtitleOpen] = useState(false);
    const [isMediaShareVisible, setIsMediaShareVisible] = useState(false);
    const [dynamicHeight, setDynamicHeight] = useState(0);
    const [topSpace, setTopSpace] = useState(0);

    let resizeObserver: IntersectionObserver = null;
    let observer: MutationObserver = null;

    const { videoItem, index, domain, showPrerollAds, currentPlayingVideo, isTitleVisible, isSubTitleVisible, isGalleryView, isMobile, shouldDisplayArrow, onNextSlide } = props;
    const { itemId, media, title, subTitle, titleLink, titleTargetLink } = videoItem;
    const dynamicWidth = dynamicHeight * ASPECT_RATIO_9_16;
    const subtitleClass = `videoSubtitle ${isSubtitleOpen ? "" : "close"}`;
    const verticalItemClass = `vertical-video-item ${isMobile ? isSubtitleOpen ? "openMode" : "standardMode" : ""}`;
    const videoPageId = (videoItem.media as SiteVideoData).videoPageId;
    const videoPageShareUrl = `${domain}/video/${videoPageId}?utm_source=ynet.co.il&utm_medium=share&utm_campaign=copy_url&utm_content=vertical_video`;
    const shouldDisplayVideo = isGalleryView && currentPlayingVideo !== undefined ? (currentPlayingVideo === index || currentPlayingVideo === index - 1 || currentPlayingVideo === index + 1) : true;

    useEffect(() => {
        // document.addEventListener('click', handleClickOutside);

        updateDynamicHeight();
        initializeResizeObserver();
        window.addEventListener("resize", updateDynamicHeight);

        observeAndMoveVideoELements();

        return () => {
            // document.removeEventListener('click', handleClickOutside);

            cleanupResizeObserver();
            window.removeEventListener("resize", updateDynamicHeight);

            observer && observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (currentPlayingVideo === index) {
            observeAndMoveVideoELements();
        }
    }, [currentPlayingVideo]);

    // const handleClickOutside = (event: MouseEvent) => {
    //     const target = event.target as Element;
    //     if (!target.closest(".vertical-video-item") && !target.closest(".slickArrow")) {
    //         props.onVideoOverlayClose();
    //     }
    // };

    const observeAndMoveVideoELements = () => {
        const targetNode = document.getElementById("VerticalVideoItem_" + itemId);

        if (!targetNode) return;

        // cleanup previous observer if exists
        if (observer) {
            observer.disconnect();
            observer = null;
        }

        observer = new MutationObserver(() => {
            // find elements that need to be moved
            const el = targetNode.querySelector(".fp-elapsed");
            const du = targetNode.querySelector(".fp-duration");
            const tl = targetNode.querySelector(".fp-timeline");
            const vol = targetNode.querySelector(".fp-volume-control");

            // find new targets
            const elTarget = targetNode.querySelector("#elapsed-target");
            const duTarget = targetNode.querySelector("#duration-target");
            const tlTarget = targetNode.querySelector("#timeline-target");
            const volTarget = targetNode.querySelector("#volume-target");

            // move elements to new targets only if all elements are found
            if (el && du && tl && vol && elTarget && duTarget && tlTarget && volTarget) {
                elTarget.innerHTML = "";
                duTarget.innerHTML = "";
                tlTarget.innerHTML = "";
                volTarget.innerHTML = "";

                elTarget.appendChild(el);
                duTarget.appendChild(du);
                tlTarget.appendChild(tl);
                volTarget.appendChild(vol);

                // stop observing after success
                if (observer) {
                    observer.disconnect();
                    observer = null;
                }
            }
        })

        observer.observe(targetNode, {
            childList: true,
            subtree: true,
        });
    }

    const initializeResizeObserver = () => {
        resizeObserver = new IntersectionObserver(() => {
            updateDynamicHeight();
        });

        const target = document.body;
        if (target && resizeObserver) {
            resizeObserver.observe(target);
        }
    }

    const cleanupResizeObserver = () => {
        if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
        }
    }

    const updateDynamicHeight = () => {
        const viewportHeight = window.innerHeight;

        const mainNavElement = document.querySelector(".BottomHeaderArea.sticky .mainNav") as HTMLElement;
        const adsSargelElement = document.getElementById("ads.sargel");
        const adsBannerElement = document.getElementById("ads.banner");

        const mainNavHeight = mainNavElement ? mainNavElement.offsetHeight : 0;
        const adsSargelHeight = adsSargelElement ? adsSargelElement.offsetHeight : 0;
        const adsBannerHeight = adsBannerElement ? adsBannerElement.offsetHeight : 0;
        const bottomMargin = adsSargelHeight + adsBannerHeight + MIN_MARGIN_FROM_HEADER_AND_FOOTER

        const dynamicHeight = viewportHeight - mainNavHeight - bottomMargin;
        const top = mainNavHeight + (MIN_MARGIN_FROM_HEADER_AND_FOOTER / 2);

        setDynamicHeight(dynamicHeight);
        setTopSpace(top);
    };

    const handleVideoEnded = (e) => {
        const { isGalleryView, onNextSlide } = props;
        e.target.currentTime = 0;
        isGalleryView ? onNextSlide() : e.target.click();
    }

    useEffect(() => {
        if (!isGalleryView || currentPlayingVideo === undefined) return;

        const handleVideoUpdate = () => {
            const videoElement = document.querySelector(".vertical-video-overlay-player .slick-active .vertical-video-item #player.is-paused .fp-engine") as HTMLVideoElement;
            const unActiveVideoElement = document.querySelector(".vertical-video-overlay-player .slick-slide:not(.slick-active) .vertical-video-item #player.is-playing .fp-engine") as HTMLVideoElement;
            if (videoElement) {
                setTimeout(() => {
                    if (videoElement.paused) {
                        // videoElement.pause();
                        videoElement.currentTime = 0;
                        videoElement.play();
                    }
                }, 500);
            }
            if (unActiveVideoElement) unActiveVideoElement.click();
        };

        const timeoutId = setTimeout(handleVideoUpdate, 600);

        return () => clearTimeout(timeoutId);
    }, [currentPlayingVideo]);

    const onSubtitleModeToggle = () => {
        setIsSubtitleOpen(!isSubtitleOpen);
    }

    const onMediaShareToggle = async () => {
        if (isMobile) {
            const { domain, videoItem: { media } } = props;
            const shareUrl = `${domain}/video/${(media as SiteVideoData).videoPageId}?utm_source=ynet.co.il&utm_medium=Share&utm_campaign=copy_url&utm_content=vertical_video`;
            const shareData = { url: shareUrl };
            try {
                // @ts-ignore 
                await navigator.share(shareData)
            } catch (err) {
                console.log("error with sharing function", err);
            }
        }
        else {
            setIsMediaShareVisible(!isMediaShareVisible);
        }
    }

    const handleShareBtnClicked = (button: string) => {
        const { domain, videoItem } = props;

        setIsMediaShareVisible(false);

        if (typeof window !== "undefined") {
            const baseUrl = `${domain}/video/${(videoItem.media as SiteVideoData).videoPageId}`;

            switch (button) {
                case "fb":
                    openFacebookShare(baseUrl, "", () => { }, "vertical_video")
                    break;
                case "tw":
                    openTwitterShare(baseUrl, () => { }, "vertical_video")
                    break;
                case "wa":
                    WhatsAppShareButton.openWhatsAppShare(baseUrl, () => { }, "vertical_video")
                    break;
            }
        }
    }

    const pushGA4Event = () => {
        if (typeof window == "undefined" || !window.dataLayer) return;
        const { index } = props;

        window.dataLayer.push({
            event: "content_click",
            componenta_name: "vertical_video",
            content_type: "componenta",
            position_in_component: index,
            click_text: "לכתבה המלאה"
        });
    };

    const NextVideoText = () => {
        const { languageCode } = props
        if (languageCode.includes("en")) return "next video"
        else if (languageCode.includes("ru")) return `Следующее видео`
        else return `לסרטון הבא`
    }

    const copyLinkText = () => {
        const { languageCode = "" } = props
        if (languageCode.includes("en")) return "copy link"
        else if (languageCode.includes("ru")) return "Скопировать ссылку"
        else return "העתק קישור"
    }

    const linkCopiedToClipboardText = () => {
        const { languageCode = "" } = props
        if (languageCode.includes("en")) return "Link copied to clipboard"
        else if (languageCode.includes("ru")) return "Ссылка скопирована"
        else return "הקישור הועתק ללוח"
    }

    const readFullArticleText = () => {
        const { languageCode = "" } = props
        if (languageCode.includes("en")) return "Read Full Article"
        else if (languageCode.includes("ru")) return `Читать полную статью`;
        else return `לכתבה המלאה`;
    }

    return (
        <div className={verticalItemClass} id={`VerticalVideoItem_${itemId}`} style={isMobile ? {} : { height: dynamicHeight, width: dynamicWidth, top: topSpace }}>
            {shouldDisplayVideo &&
                <SiteVideoInline
                    isShareButton={false}
                    applyStickyness={true}
                    data={media as SiteVideoData}
                    runArticleAutoPlayLogic={false}
                    onVideoEnded={handleVideoEnded}
                    adIndex={videoItem.prerollIndex}
                    width={isMobile ? null : dynamicWidth}
                    height={isMobile ? null : dynamicHeight}
                    hideVideoAds={showPrerollAds ? videoItem.hidePrerollAds : true}
                />
            }

            <div className="contentDiv">
                <div className="videoInfo">
                    <div className="textContent">
                        {isTitleVisible && <div className="videoTitle" dangerouslySetInnerHTML={{ __html: title }} />}

                        {isSubTitleVisible && <div className={subtitleClass} onClick={onSubtitleModeToggle} dangerouslySetInnerHTML={{ __html: subTitle }} />}

                        <div className="moreDetails">
                            <div className="credit">{media.credit}</div>

                            {titleLink &&
                                <div onClick={pushGA4Event}>
                                    <HyperLinkBuilder className="videoLink" href={titleLink} linkTarget={titleTargetLink}>
                                        <div className="linkText">{readFullArticleText()}</div>
                                        <div className="linkIcon"></div>
                                    </HyperLinkBuilder>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="btnsContent">
                        {videoPageId &&
                            <div className="shareArea">
                                <div className="shareBtn" onClick={onMediaShareToggle}></div>

                                {isMediaShareVisible &&
                                    <div className="mediaShareWrapper">
                                        <div className="mediaShareItem" onClick={() => handleShareBtnClicked("fb")}>
                                            <div className="mediaShareIcon facebook"></div>
                                            <div className="mediaShareText">Facebook</div>
                                        </div>
                                        <div className="mediaShareItem" onClick={() => handleShareBtnClicked("wa")}>
                                            <div className="mediaShareIcon whatsapp"></div>
                                            <div className="mediaShareText">WhatsApp</div>
                                        </div>
                                        <div className="mediaShareItem" onClick={() => handleShareBtnClicked("tw")}>
                                            <div className="mediaShareIcon twitter"></div>
                                            <div className="mediaShareText">Twitter</div>
                                        </div>
                                        <div className="mediaShareItem">
                                            <FrontCopyButton popupText={linkCopiedToClipboardText()} title={title} url={videoPageShareUrl} />
                                            <div className="mediaShareText">{copyLinkText()}</div>
                                        </div>
                                    </div>
                                }
                            </div>
                        }

                        <div className="mute-btn" id="volume-target"></div>
                    </div>
                </div>

                <div className="playerDetails">
                    <div className="timeline" id="timeline-target"></div>

                    <div className="playing-time">
                        <div className="elapsed" id="elapsed-target"></div>
                        /
                        <div className="duration" id="duration-target"></div>
                    </div>

                    {shouldDisplayArrow &&
                        <div className={`arrow arrowNext`} onClick={onNextSlide}>
                            <div className="arrowText">{NextVideoText()}</div>
                            <div className="arrowIcon"></div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};